import { ReactElement, useCallback } from 'react';
import { Button, ButtonProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { getChainIdByEnv } from 'modules/api';
import { currentEnv } from 'modules/common';
import { useTranslation } from 'modules/i18n';

import { useConnection } from '../../hooks/useConnection';
import { getIotaWalletById } from '../../utils/getIotaWalletById';
import { translation } from './translation';

interface IConnectButtonProps extends ButtonProps {
  icon: React.ReactElement;
  name: string;
  walletId: string;
  installWalletLink: string;
}

export function ConnectButton({
  icon,
  name,
  walletId,
  installWalletLink,
  ...rest
}: IConnectButtonProps): ReactElement {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(translation);
  const { connect, isLoading } = useConnection();

  const isWalletInjected = getIotaWalletById(
    walletId,
    getChainIdByEnv(currentEnv),
  );

  const handleConnect = useCallback(async () => {
    await connect(walletId);
  }, [connect, walletId]);

  if (isWalletInjected) {
    return (
      <Button
        className={classes.root}
        disabled={isLoading}
        variant="outlined"
        onClick={handleConnect}
        {...rest}
      >
        <div className={classes.wallet}>
          {icon}

          <div>{name}</div>
        </div>

        <div className={classes.action}>
          {isWalletInjected ? t(keys.connect) : t(keys.install)}
        </div>
      </Button>
    );
  }

  return (
    <Button
      className={classes.root}
      component="a"
      disabled={isLoading}
      href={installWalletLink}
      rel="noreferrer"
      target="_blank"
      variant="outlined"
      {...rest}
    >
      <div className={classes.wallet}>
        {icon}

        <div>{name}</div>
      </div>

      <div className={classes.action}>
        {isWalletInjected ? t(keys.connect) : t(keys.install)}
      </div>
    </Button>
  );
}

export const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.main}`,
    fontSize: 16,
    width: '100%',
    borderRadius: 78,
    height: 68,
    fontWeight: 400,
    color: theme.palette.secondary.contrastText,
  },
  icon: {
    width: 36,
  },
  wallet: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  action: {
    color: theme.palette.primary.main,
  },
}));
